import { initRouter } from './router.js';
import { headerShell, initNavbar } from './components/navbar.js';
import { initFooter, markFooterReady } from './components/footer.js';
import { lightboxHtml, initLightbox } from './components/lightbox.js';
import { getPages } from './api.js';
import { initAnimations } from './components/animate.js';

// Routes that always exist, independent of what's published in WP — the
// home grid and individual project pages aren't WordPress Page-backed
// (see portfolio-spa-plan.md §2a: the `project` CPT is unchanged, and `/`
// still has its own hardcoded view for now).
const staticRoutes = [
  {
    path: '/',
    view: () => import('./views/list-views.js').then((m) => ({
      default: m.home,
      renderShell: m.homeShell,
    })),
    title: 'Jonas Wendt Knudsen (JWK) – Web Developer, Copenhagen',
    description: 'Jonas Wendt Knudsen (JWK) is a web developer in Copenhagen building WordPress and JavaScript websites. See projects like CarsWiki.',
    breadcrumbs: [{ name: 'Home', path: '/' }],
  },
  {
    path: '/projects/:slug',
    view: () => import('./views/detail-views.js').then((m) => ({ default: m.projectDetail })),
    title: (params) => `${params.slug} — jwk.dk`,
    description: (params) => `${params.slug} — a project by Jonas Wendt Knudsen.`,
    breadcrumbs: (params) => [
      { name: 'Home', path: '/' },
      { name: params.slug, path: `/projects/${params.slug}` },
    ],
  },
];

const notFoundRoute = {
  path: '*',
  view: () => import('./views/detail-views.js').then((m) => ({ default: m.notFound })),
  title: 'Page not found — jwk.dk',
  description: 'This page does not exist.',
  breadcrumbs: null,
};

// Slugs a WordPress Page isn't allowed to claim a route for: either a
// static route above already owns that path ("projects"), or the slug is
// reserved for a future purpose that isn't wired up yet ("home" mapping
// onto `/` itself — still open, see portfolio-spa-plan.md §7). A Page
// using one of these slugs is simply skipped, not an error.
const RESERVED_PAGE_SLUGS = new Set(['projects', 'home']);

// Emergency fallback only: used if the WP Pages fetch itself fails (WP
// down, network hiccup) so /about and /contact don't go dead. On any
// successful fetch, a real "about"/"contact" Page in WP always wins
// instead — these two static views only render if that fetch errors.
function fallbackRoutes() {
  return [
    {
      path: '/about',
      view: () => import('./views/detail-views.js').then((m) => ({ default: m.about })),
      title: 'About — jwk.dk',
      description: 'About Jonas Wendt Knudsen.',
      breadcrumbs: [
        { name: 'Home', path: '/' },
        { name: 'About', path: '/about' },
      ],
    },
    {
      path: '/contact',
      view: () => import('./views/detail-views.js').then((m) => ({ default: m.contact })),
      title: 'Contact — jwk.dk',
      description: 'Get in touch.',
      breadcrumbs: [
        { name: 'Home', path: '/' },
        { name: 'Contact', path: '/contact' },
      ],
    },
  ];
}

// One route per published WP Page (any slug not reserved above), content
// and title driven entirely by wp-admin/Gutenberg — see
// portfolio-spa-plan.md §2a. Adding a new Page in wp-admin adds a new
// live route here with no code change. Assumes flat (non-hierarchical)
// page slugs — a Page with a WP parent still routes off its own leaf
// slug, not a nested path.
function buildPageRoutes(pages) {
  return pages
    .filter((page) => !RESERVED_PAGE_SLUGS.has(page.slug))
    .map((page) => {
      const title = page.title.rendered;
      return {
        path: `/${page.slug}`,
        view: () => import('./views/detail-views.js').then((m) => ({
          default: m.createPageView(page.slug),
        })),
        title: `${title} — jwk.dk`,
        description: `${title} — Jonas Wendt Knudsen.`,
        breadcrumbs: [
          { name: 'Home', path: '/' },
          { name: title, path: `/${page.slug}` },
        ],
      };
    });
}

async function buildRoutes() {
  try {
    const pages = await getPages();
    return [...staticRoutes, ...buildPageRoutes(pages)];
  } catch (err) {
    console.warn(
      'Could not fetch WordPress pages — falling back to static /about and /contact:',
      err
    );
    return [...staticRoutes, ...fallbackRoutes()];
  }
}

async function initApp() {
  // The header's own markup (logo, nav links, mobile toggle) comes from
  // navbar.js, not static HTML in index.html — rendered synchronously,
  // before any async work, so it still paints essentially immediately.
  const header = document.getElementById('header');
  header.innerHTML = headerShell();

  // The navbar's secondary row (currently just a "N projects" counter,
  // see navbar.js) is switched off for now — no real structure to hang it
  // off yet. navbarSecondaryRow() is still there, unused, for when it's
  // needed again; just re-add the fetch/insert below to bring it back.
  initFooter();
  document.body.insertAdjacentHTML('beforeend', lightboxHtml());

  // Kicked off before anything awaits it, so the WP Pages fetch this
  // needs overlaps with the synchronous DOM work above and with
  // initNavbar() below, rather than adding a strictly serial round trip
  // on top of them. The router can't start matching until this resolves,
  // though — it needs the final route table (including any WP Page
  // routes) before the very first loadRoute(), or a cold direct load of
  // e.g. /about would flash "not found" before flipping to real content.
  const routerPromise = buildRoutes().then((routes) => initRouter(routes, notFoundRoute));

  window.addEventListener('route:rendered', () => {
    initLightbox();
    markFooterReady();
    initAnimations();
  });

  header.classList.add('header-ready');
  initNavbar();

  await routerPromise;

  // Non-critical background work goes here, gated on requestIdleCallback
  // so it never competes with LCP. Example hooks: analytics init, warming
  // a secondary cache, exposing site data to a browser extension/agent.
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
  idle(() => {
    // e.g. initAnalytics();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}
